"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { getSupabaseBrowserClient } from "@/utils/supabase/client";

function postBeacon(event: string, payload: Record<string, unknown>): void {
  try {
    const url = `/api/telemetry/${event}`;
    const body = JSON.stringify(payload);

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
      // sendBeacon can return false on quota / CSP / >64KB — fall through to fetch.
    }

    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body,
    });
  } catch {
    // telemetry must never throw into UI
  }
}

export function AuthTelemetry({ initialUserId }: { initialUserId: string | null }) {
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const lastKnownUserId = useRef<string | null>(initialUserId);
  const sawInitialEvent = useRef(false);

  // Subscribe once on mount. usePathname is read via ref so we don't
  // resubscribe on every navigation (which would race subscribe/unsubscribe
  // and drop events fired during the gap).
  useEffect(() => {
    const supabase = getSupabaseBrowserClient();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      const incomingId = session?.user?.id ?? null;
      const currentPathname = pathnameRef.current ?? (typeof window !== "undefined" ? window.location.pathname : "");

      // The supabase client emits an INITIAL_SESSION event on mount. Use it
      // to validate hydration: if the server said we were authenticated
      // (initialUserId !== null) but the client mounted with no session,
      // that's a session-loss-on-hydration — log it as its own event,
      // because the simple SIGNED_OUT path won't fire in this case.
      if (!sawInitialEvent.current) {
        sawInitialEvent.current = true;
        if (initialUserId && incomingId === null) {
          postBeacon("mount_no_session", {
            initial_user_id: initialUserId,
            pathname: currentPathname,
            referrer: typeof document !== "undefined" ? document.referrer || null : null,
            event_type: event,
          });
        }
        lastKnownUserId.current = incomingId;
        return;
      }

      if (event === "SIGNED_OUT" && lastKnownUserId.current) {
        postBeacon("client_signed_out", {
          prev_user_id: lastKnownUserId.current,
          pathname: currentPathname,
          referrer: typeof document !== "undefined" ? document.referrer || null : null,
          visibility: typeof document !== "undefined" ? document.visibilityState : null,
        });
      }

      lastKnownUserId.current = incomingId;
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, [initialUserId]);

  return null;
}
